import { NetworkProviderFilterType, ProviderDropdownTypes } from "@app/exports/enumerations";
import { ExportImpactProviderTypeEnum } from "@app/exports/exportModels";
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { QESCheckbox, QESRadioButton, QESSelect } from "@app/shared/qes-standards";
import { Roles, UserContext } from "@app/user";
import { FC, JSX, useContext, useEffect, useMemo } from "react";
import { VolumeReportsContext } from "./VolumeReportsContext";
import { toast } from "react-toastify";
import { WizardStepProps } from "./interfaces";
import { ExportService } from "../../../exports";

export const VolumeReportsProvidersFilter: FC<React.PropsWithChildren<WizardStepProps>> = ( props: WizardStepProps ) => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const wizardContext = useContext(VolumeReportsContext);
  const userRoles = useContext(UserContext).roles;
  const exportService = new ExportService();
  const selectedProjectCountySpecialtyCount = wizardContext.projects.filter((project) => project.isChecked).reduce((previous, current) => previous + current.countySpecialtyGroupCount , 0);

  useEffect(() => {
    switch (wizardContext.providerType) {
      case ProviderNetworkType.Network:
        exportService.getNetworkServicingProviderUploadLimit().then(limit => {
          if (selectedProjectCountySpecialtyCount >= limit) {
            toast(getUploadLimitMessage(limit),
              {
                containerId: "z-index-9999",
                type: "error"
              });
          }
          props.pageIsValid(selectedProjectCountySpecialtyCount < limit)
        });
        break;
      case ProviderNetworkType.Market:
      case ProviderNetworkType.Prospect:
        exportService.getProspectMarketServicingProviderUploadLimit().then(limit => {
          if (selectedProjectCountySpecialtyCount >= limit) {
            toast(getUploadLimitMessage(limit),
              {
                containerId: "z-index-9999",
                type: "error"
              });
          }
          props.pageIsValid(selectedProjectCountySpecialtyCount < limit)
        });
        break;
    }
  }, [wizardContext.providerType]);

  const filterOptions = useMemo(() => {
    return (
      <>
        <option key={ProviderDropdownTypes.Network} value={ProviderDropdownTypes.Network}>Network Servicing Providers</option>
        <option key={ProviderDropdownTypes.Prospect} value={ProviderDropdownTypes.Prospect}>Prospect Servicing Providers</option>
        <option key={ProviderDropdownTypes.Market} value={ProviderDropdownTypes.Market}>Market Servicing Providers</option>
        {userRoles.includes(Roles.NET_NEW_PROVIDER_USER) && wizardContext.hasNetNewProviders && <option key={ProviderDropdownTypes.NetNew} value={ProviderDropdownTypes.NetNew}>Net New Servicing Providers</option>}
        {featureFlags.has(Features.NCP_VOLUME_REPORTS) && wizardContext.isProjectWithNcpSelected && <option key={ProviderDropdownTypes.NextClosestProviders} value={ProviderDropdownTypes.NextClosestProviders}>Next Closest Providers</option>}
      </>)
  }, [featureFlags, wizardContext.isProjectWithNcpSelected])

  const radioOptions = useMemo(() => {
    // the context also resets the provider radio option to all providers in the context
    const options: JSX.Element[] = [];
    switch (wizardContext.providerType) {
      case ProviderNetworkType.Network:
        options.push(
          <QESRadioButton
            key={NetworkProviderFilterType.Available}
            label="All Providers"
            name="provider-filter"
            value={NetworkProviderFilterType.Available}
            checked={wizardContext.networkProviderFilterType === NetworkProviderFilterType.Available}
            onChange={(e) => wizardContext.setNetworkProviderFilterType(e.target.value as NetworkProviderFilterType)}
            testId="volume-reports__network-provider-filter__all"
          />
        );
        if (userRoles.includes(Roles.TERM_PROVIDER_ADMIN) || userRoles.includes(Roles.TERM_PROVIDER_VIEWER)) {
          options.push(
            <QESRadioButton
              key={NetworkProviderFilterType.Unterminated}
              label="Network Minus Terminated Providers"
              name="provider-filter"
              value={NetworkProviderFilterType.Unterminated}
              checked={wizardContext.networkProviderFilterType === NetworkProviderFilterType.Unterminated}
              onChange={(e) => wizardContext.setNetworkProviderFilterType(e.target.value as NetworkProviderFilterType)}
              testId="volume-reports__network-provider-filter__unterminated"
            />,
            <QESRadioButton
              key={NetworkProviderFilterType.Terminated}
              label="Terminated Providers"
              name="provider-filter"
              value={NetworkProviderFilterType.Terminated}
              checked={wizardContext.networkProviderFilterType === NetworkProviderFilterType.Terminated}
              onChange={(e) => wizardContext.setNetworkProviderFilterType(e.target.value as NetworkProviderFilterType)}
              testId="volume-reports__network-provider-filter__terminated"
            />
          )
        }
        break;
      case ProviderNetworkType.Market:
      case ProviderNetworkType.Prospect:
      case ProviderNetworkType.NetNew:
        /**
         * The `checked` property on these is funky because TS numeric enums compile to
         * an array of <string, number> tuples, but then when it's rendered in HTML
         * the number is converted to a string. But in TS we're saving it as an enum.
         * So the compiled version is checking if (string)4 === (number)4, and it's not. 
         * Or something like that.
         * So we toString() both of them.
         * If we only toString() the (number) one TS won't compile because 
         * you "can't compare an enum with a string."
         */
        options.push(
          <QESRadioButton
            key={ExportImpactProviderTypeEnum.All}
            label="All Providers"
            name="provider-filter"
            value={ExportImpactProviderTypeEnum.All}
            checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.All.toString()}
            onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
            testId="volume-reports__impact-provider-filter__all"
          />,
          <QESRadioButton
            key={ExportImpactProviderTypeEnum.Targeted}
            label="Targeted Providers"
            name="provider-filter"
            value={ExportImpactProviderTypeEnum.Targeted}
            checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.Targeted.toString()}
            onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
            testId="volume-reports__impact-provider-filter__targeted"
          />
        );

        // Add available and excluded provider options for Market and Prospect networks.
        if ([ProviderNetworkType.Market, ProviderNetworkType.Prospect].includes(wizardContext.providerType)) {
          options.push(
            <QESRadioButton
              key={ExportImpactProviderTypeEnum.Available}
              label="Available Providers"
              name="provider-filter"
              value={ExportImpactProviderTypeEnum.Available}
              checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.Available.toString()}
              onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
              testId="volume-reports__impact-provider-filter__available"
            />,
            <QESRadioButton
              key={ExportImpactProviderTypeEnum.Excluded}
              label="Excluded Providers"
              name="provider-filter"
              value={ExportImpactProviderTypeEnum.Excluded}
              checked={wizardContext.impactProviderType.toString() === ExportImpactProviderTypeEnum.Excluded.toString()}
              onChange={(e) => { wizardContext.setImpactProviderType(e.target.value as unknown as ExportImpactProviderTypeEnum); }}
              testId="volume-reports__impact-provider-filter__excluded"
            />
          );
        }
        break;
    }
    return options;
  }, [wizardContext.providerType, wizardContext.impactProviderType, wizardContext.networkProviderFilterType])

  const getUploadLimitMessage = (limit: number): string => {
    return `Your selections exceed the ${limit} threshold, which may cause delays or incomplete results. To ensure faster processing and accuracy, please reduce your selection by adjusting your criteria.`; 
  }

  return (
    <section>
      <h2 className="header-m">Provider Filter</h2>
      <p>Please select the providers to include:</p>
      <QESSelect
        label=""
        hideLabel={true}
        containerClassName="mb-2"
        testId="volume-reports__provider-type"
        value={wizardContext.providerType}
        onChange={(e) => {
          if (e.target.value !== ProviderNetworkType.Network && wizardContext.appendPatternOfCareProviders) {
            wizardContext.setAppendPatternOfCareProviders(false);
            toast("\"Append Pattern of Care Providers\" was unchecked. Select Network Providers in the Provider Filter and MA HSD Format to Append Pattern of Care Providers.", {
              containerId: "z-index-1030",
              type: "info",
            });
          }
          wizardContext.setProviderType(e.target.value as ProviderNetworkType);
        }}
      >
        {filterOptions}
      </QESSelect>
      {wizardContext.isProjectWithNcpSelected && wizardContext.providerType === ProviderNetworkType.Network && (
        <QESCheckbox
          label="Append Next Closest Network Providers"
          checked={wizardContext.appendNcp}
          onChange={(e) => { wizardContext.setAppendNcp(e.target.checked); }}
          testId="volume-reports__append-ncp"
          containerClassName="mb-3"
        />
      )}
      {wizardContext.providerType.toString() !== ProviderDropdownTypes.NextClosestProviders.toString() &&
        <div className="ml-3 mt-4 qes-radio-group__container">
          <p className="mb-1">Filter</p>
          {radioOptions}
        </div>
      }
    </section>
  );
}